import React, { useEffect, useRef, useState } from 'react'
import './fadetext.css'

//
// Text that fades out as it enters and exits.
//
// Example:
//
//	<FadeText>Hi there</FadeText>
//
// ... or for <div> instead of <span>:
//
//	<FadeText isBlock={true}>
//		<p>etc</p>
//		<p>etc</p>
//	</FadeText>
//
// Optional: Supply a 'key' so we don't need to calculate one.
//

const DURATION = 160

type FadeTextProps = {
	children: any
	className?: string
	isBlock?: boolean
	contentKey?: string
}

const FadeText = (props: React.PropsWithChildren<FadeTextProps>) => {

	const { children, className, contentKey } = props

	// console.log('FadeText', className, contentKey, typeof children)

	const [ hide, setHide ] = useState(true)
	const [ displayedContent, setDisplayedContent ] = useState('')
	const [ key, setKey ] = useState('')
	const pendingContent = useRef<any>(children)

	useEffect(() => {
		const newKey = contentKey || jsxToString(children)

		if (key !== newKey) {
			pendingContent.current = children
			setKey(newKey)
			setHide(true)
		} else if (!hide) {
			setDisplayedContent(children)
		}
	}, [ children, key, contentKey, hide ])

	useEffect(() => {
		let timer: ReturnType<typeof setTimeout>

		if (key !== '') {
			timer = setTimeout(() => {
				setHide(false)
			}, DURATION)
		}

		return () => clearTimeout(timer)
	}, [ key ])

	useEffect(() => {
		if (!hide) {
			const newContent = (pendingContent.current === undefined || pendingContent.current === null || pendingContent.current === false)
				? ''
				: pendingContent.current
			setDisplayedContent(newContent)
		}
	}, [ hide ])

    const myClasses = [ 'fade-text' ]
    if (hide) {
        myClasses.push('hide')
    }
    if (className) {
        myClasses.push(className)
    }

    const myClassName = myClasses.join(' ')

    if (props.isBlock) {
        return (
            <div className={myClassName}>
                {displayedContent}
            </div>
        )
    } else {
        return (
            <span className={myClassName}>
                {displayedContent}
            </span>
        )
    }
}

function jsxToString(jsx: any): string {

	if (jsx === null || jsx === undefined) {
		return ''
	}

	if (typeof jsx === 'string') {
		return jsx
	}

	if (typeof jsx === 'number') {
		return String(jsx)
	}

    if (typeof jsx === 'boolean') {
        return jsx ? '1' : '0'
    }

	if (typeof jsx === 'object') {
		const isArray = Array.isArray(jsx)
		if (isArray) {
			return jsx.map(item => jsxToString(item)).join(' ')
		}
		if (jsx.props) {
			if (jsx.props.value !== undefined) {
				if (jsx.props.value === null) {
					return ''
				}
				return jsx.props.value
			}
			if (jsx.props.children) {
				return jsxToString(jsx.props.children)
			}
		}
	}

	console.error("FadeText needs a manually supplied contentKey, because we can't derive one from this", typeof jsx, jsx)
	return ''
}

export default FadeText
